import React, { useEffect, useRef } from 'react';
export function CyberpunkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    // Initialize mouse at center
    mouseRef.current = {
      x: width / 2,
      y: height / 2
    };
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };
    window.addEventListener('mousemove', handleMouseMove);
    // 3D Configuration
    // Increased particle count (was 60/130, now 80/170)
    const particleCount = width < 768 ? 80 : 170;
    const connectionDistance = 250;
    const fov = 500;
    // Rotation state
    let rotationX = 0;
    let rotationY = 0;
    // Constant slow drift
    let driftY = 0;
    class Particle3D {
      x: number;
      y: number;
      z: number;
      color: string;
      size: number;
      constructor() {
        const spread = 1000;
        this.x = (Math.random() - 0.5) * spread * 2;
        this.y = (Math.random() - 0.5) * spread * 2;
        this.z = (Math.random() - 0.5) * spread * 2;
        this.color = Math.random() > 0.5 ? '#00d4ff' : '#ff00ff';
        this.size = Math.random() * 4 + 2;
      }
      project(rotX: number, rotY: number) {
        // 1. Rotate around Y axis
        let x1 = this.x * Math.cos(rotY) - this.z * Math.sin(rotY);
        let z1 = this.z * Math.cos(rotY) + this.x * Math.sin(rotY);
        // 2. Rotate around X axis
        let y1 = this.y * Math.cos(rotX) - z1 * Math.sin(rotX);
        let z2 = z1 * Math.cos(rotX) + this.y * Math.sin(rotX);
        // 3. Apply perspective
        const scale = fov / (fov + z2);
        const x2d = x1 * scale + width / 2;
        const y2d = y1 * scale + height / 2;
        return {
          x: x2d,
          y: y2d,
          scale,
          z: z2
        };
      }
    }
    const particles: Particle3D[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle3D());
    }
    function animate() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      const targetRotY = (mouseRef.current.x - width / 2) * 0.0003;
      const targetRotX = (mouseRef.current.y - height / 2) * 0.0003;
      rotationY += (targetRotY - rotationY) * 0.05;
      rotationX += (targetRotX - rotationX) * 0.05;
      driftY += 0.0005;
      const finalRotY = rotationY + driftY;
      const finalRotX = rotationX;
      const projectedPoints: {
        x: number;
        y: number;
        z: number;
        color: string;
        size: number;
        scale: number;
      }[] = [];
      // Project all particles
      particles.forEach(p => {
        const point = p.project(finalRotX, finalRotY);
        if (point.z > -fov + 20) {
          const depthScale = Math.pow(point.scale, 1.4);
          const calculatedSize = p.size * depthScale;
          const finalSize = Math.min(calculatedSize, 18);
          projectedPoints.push({
            x: point.x,
            y: point.y,
            z: point.z,
            color: p.color,
            size: finalSize,
            scale: point.scale
          });
        }
      });
      projectedPoints.sort((a, b) => b.z - a.z);
      // Draw connections
      ctx.lineWidth = 1;
      for (let i = 0; i < projectedPoints.length; i++) {
        const p1 = projectedPoints[i];
        for (let j = i + 1; j < projectedPoints.length; j++) {
          const p2 = projectedPoints[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            const opacity = 1 - dist / connectionDistance;
            const gradient = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
            gradient.addColorStop(0, p1.color);
            gradient.addColorStop(1, p2.color);
            ctx.strokeStyle = gradient;
            ctx.globalAlpha = opacity * 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      // Draw particles
      projectedPoints.forEach(p => {
        ctx.globalAlpha = Math.min(1, Math.max(0.4, p.scale));
        ctx.fillStyle = p.color;
        const size = Math.max(1, p.size);
        ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
      });
      requestAnimationFrame(animate);
    }
    animate();
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none bg-black" data-id="element-124" />;
}